import React from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Button, CircularLoader, AlertBar } from '@dhis2/ui';

const OrgUnitChip = ({ orgUnit, isAssigned, onAssign, onUnassign, isDraggable = true }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'ORG_UNIT',
    item: { orgUnit },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => isDraggable,
  }));

  return (
    <div
      ref={isDraggable ? drag : null}
      style={{
        padding: '8px 12px',
        backgroundColor: isAssigned ? '#4caf50' : '#f5f5f5',
        color: isAssigned ? 'white' : '#333',
        borderRadius: '20px',
        cursor: isDraggable ? 'move' : 'default',
        margin: '4px',
        display: 'inline-block',
        opacity: isDragging ? 0.5 : 1,
        border: `1px solid ${isAssigned ? '#388e3c' : '#ddd'}`,
        transition: 'all 0.2s ease',
      }}
      onClick={() => {
        if (isDraggable) {
          isAssigned ? onUnassign(orgUnit.id) : onAssign(orgUnit.id);
        }
      }}
    >
      {orgUnit.name}
      {isAssigned && ' ✓'}
    </div>
  );
};

const ProgramDropZone = ({ program, assignedOrgUnits, onDrop, hasUnassignedOrgs }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'ORG_UNIT',
    drop: (item) => onDrop(program.id, item.orgUnit),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  return (
    <div
      ref={drop}
      style={{
        padding: '16px',
        backgroundColor: isOver ? '#e3f2fd' : '#fafafa',
        border: `2px dashed ${hasUnassignedOrgs ? '#f44336' : isOver ? '#2196f3' : '#bdbdbd'}`,
        borderRadius: '8px',
        marginBottom: '16px',
        minHeight: '100px',
        transition: 'all 0.2s ease',
      }}
    >
      <h4 style={{ margin: '0 0 12px 0', color: '#1976d2' }}>
        {program.name} {hasUnassignedOrgs && '⚠️'}
      </h4>
      <div style={{ minHeight: '60px' }}>
        {assignedOrgUnits.length === 0 ? (
          <p style={{ color: '#757575', fontStyle: 'italic', margin: 0 }}>
            {hasUnassignedOrgs ? 'Missing assignments! Drag org units here' : 'Drag organization units here to assign'}
          </p>
        ) : (
          assignedOrgUnits.map(orgUnit => (
            <OrgUnitChip
              key={orgUnit.id}
              orgUnit={orgUnit}
              isAssigned={true}
              onUnassign={(orgUnitId) => onDrop(program.id, { id: orgUnitId }, true)}
              isDraggable={true}
            />
          ))
        )}
      </div>
    </div>
  );
};

const OrganisationAssignDrop = ({
  programs,
  orgUnits,
  programAssignments,
  onAssignmentChange,
  onSaveAssignments,
  loading = false,
  currentUserOrgUnitId
}) => {
  // Filter to show only children and descendants of current user's org unit
  const filteredOrgUnits = orgUnits.filter(ou => 
    ou.path?.includes(currentUserOrgUnitId) || ou.id === currentUserOrgUnitId
  );

  // Filter to show only Performance Management Monitoring program
  const performancePrograms = programs.filter(program => 
    program.name === 'Performance Management Monitoring'
  );

  // Don't render anything if no Performance Management program exists
  if (performancePrograms.length === 0) {
    return (
      <AlertBar info>
        No Performance Management program found. Please create the program first.
      </AlertBar>
    );
  }

  const getAssignedOrgUnitsForProgram = (programId) => {
    const assignedIds = programAssignments[programId] || [];
    return filteredOrgUnits.filter(ou => assignedIds.includes(ou.id));
  };

  const getAllAssignedOrgUnits = () => {
    const allAssignedIds = new Set();
    Object.values(programAssignments).forEach(orgUnitIds => {
      orgUnitIds.forEach(id => allAssignedIds.add(id));
    });
    return filteredOrgUnits.filter(ou => allAssignedIds.has(ou.id));
  };

  const getUnassignedOrgUnits = () => {
    const allAssignedIds = new Set();
    Object.values(programAssignments).forEach(orgUnitIds => {
      orgUnitIds.forEach(id => allAssignedIds.add(id));
    });
    return filteredOrgUnits.filter(ou => !allAssignedIds.has(ou.id));
  };

  const hasUnassignedOrganisations = () => {
    return getUnassignedOrgUnits().length > 0;
  };
  

  const handleProgramDrop = (programId, orgUnit, unassign = false) => {
    const newAssignments = { ...programAssignments };
    
    if (unassign) {
      // Remove from all programs
      Object.keys(newAssignments).forEach(pId => {
        newAssignments[pId] = newAssignments[pId].filter(id => id !== orgUnit.id);
        if (newAssignments[pId].length === 0) {
          delete newAssignments[pId];
        }
      });
    } else {
      // Remove from other programs first (single program assignment)
      Object.keys(newAssignments).forEach(pId => {
        if (pId !== programId) {
          newAssignments[pId] = newAssignments[pId].filter(id => id !== orgUnit.id);
          if (newAssignments[pId].length === 0) {
            delete newAssignments[pId];
          }
        }
      });
      
      // Add to target program
      newAssignments[programId] = [...(newAssignments[programId] || []), orgUnit.id];
    }
    
    onAssignmentChange(newAssignments);
  };

  const unassignedOrgs = getUnassignedOrgUnits();
  const assignedOrgs = getAllAssignedOrgUnits();
  const hasUnassigned = unassignedOrgs.length > 0;

  return (
    <DndProvider backend={HTML5Backend}>
      {/* Warning Alert for unassigned org units */}
      {hasUnassigned && (
        <AlertBar warning style={{ marginBottom: '16px' }}>
          ⚠️ {unassignedOrgs.length} organization unit(s) are not assigned to any program. 
          Please assign all organization units before proceeding.
        </AlertBar>
      )}

      {/* Summary Stats */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        marginBottom: '16px',
        padding: '8px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px'
      }}>
        <span>Total Department/Sections Units: <strong>{filteredOrgUnits.length}</strong></span>
        <span>Assigned: <strong style={{color: '#4caf50'}}>{assignedOrgs.length}</strong></span>
        <span>Unassigned: <strong style={{color: hasUnassigned ? '#f44336' : '#4caf50'}}>{unassignedOrgs.length}</strong></span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Available Organization Units */}
        <div>
          <h4>Available Departments/Sections ({unassignedOrgs.length})</h4>
          <div style={{ 
            padding: '16px', 
            backgroundColor: hasUnassigned ? '#fff3e0' : '#f8f9fa',
            borderRadius: '8px',
            minHeight: '200px',
            border: `2px solid ${hasUnassigned ? '#ff9800' : '#dee2e6'}`
          }}>
            {unassignedOrgs.map(orgUnit => (
              <OrgUnitChip
                key={orgUnit.id}
                orgUnit={orgUnit}
                isAssigned={false}
                onAssign={(orgUnitId) => handleProgramDrop(performancePrograms[0].id, { id: orgUnitId })}
                onUnassign={(orgUnitId) => handleProgramDrop(performancePrograms[0].id, { id: orgUnitId }, true)}
              />
            ))}
            
            {unassignedOrgs.length === 0 && (
              <p style={{ color: '#28a745', fontStyle: 'italic', margin: 0 }}>
                All organization units are assigned! ✓
              </p>
            )}
          </div>
        </div>

        {/* Program Assignment Zones - Only show Performance Management */}
        <div>
          <h4>Performance Assignments ({assignedOrgs.length} assigned)</h4>
          <p>⚠️ IMPORTANT:  Ensure to press Save All Performance Assignment Button below after Assignment. </p>
          {performancePrograms.map(program => {
            const assignedToProgram = getAssignedOrgUnitsForProgram(program.id);
            return (
              <ProgramDropZone
                key={program.id}
                program={program}
                assignedOrgUnits={assignedToProgram}
                onDrop={handleProgramDrop}
                hasUnassignedOrgs={hasUnassigned}
              />
            );
          })}
          
          {/* Show assigned org units count */}
          <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
            <strong>Assignment Summary:</strong>
            {performancePrograms.map(program => {
              const assignedCount = getAssignedOrgUnitsForProgram(program.id).length;
              return (
                <div key={program.id} style={{ marginTop: '4px' }}>
                  {program.name}: <strong>{assignedCount}</strong> unit(s) assigned
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Button 
        primary
        onClick={onSaveAssignments}
        disabled={loading || hasUnassigned}
        style={{ 
          marginTop: 20, 
          width: '100%',
          backgroundColor: hasUnassigned ? '#ccc' : undefined
        }}
      >
        {loading ? <CircularLoader small /> : 
          hasUnassigned ? `Assign ${unassignedOrgs.length} remaining unit(s)` : 'Save All Performance Assignments'
        }
      </Button>
    </DndProvider>
  );
};

export default OrganisationAssignDrop;  